import { useMutation, useQueryClient } from '@tanstack/react-query';

import axios from '@/portainer/services/axios/axios';
import { EnvironmentId } from '@/react/portainer/environments/types';
import { getAllSettledItems } from '@/portainer/helpers/promise-utils';
import { withError } from '@/react-tools/react-query';
import { notifyError, notifySuccess } from '@/portainer/services/notifications';
import { pluralize } from '@/portainer/helpers/strings';

import { queryKeys } from './query-keys';

export function useDeleteCustomResourceDefinition(
  environmentId: EnvironmentId
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (definitionNames: string[]) =>
      deleteCustomResourceDefinitions(definitionNames, environmentId),
    onSuccess: ({ fulfilledItems, rejectedItems }) => {
      // one error notification per rejected item
      rejectedItems.forEach(({ item, reason }) => {
        notifyError(
          `Failed to remove custom resource definition '${item}'`,
          new Error(reason)
        );
      });

      // one success notification for all fulfilled items
      if (fulfilledItems.length) {
        notifySuccess(
          `${pluralize(
            fulfilledItems.length,
            'Custom Resource Definition'
          )} successfully removed`,
          fulfilledItems.join(', ')
        );
      }
      queryClient.invalidateQueries(
        queryKeys.customResourceDefinitions(environmentId)
      );
    },
    ...withError('Unable to remove custom resource definitions'),
  });
}

function deleteCustomResourceDefinitions(
  definitionNames: string[],
  environmentId: EnvironmentId
) {
  return getAllSettledItems(definitionNames, deleteCustomResourceDefinition);

  async function deleteCustomResourceDefinition(definitionName: string) {
    try {
      await axios.delete(
        `/kubernetes/${environmentId}/customresourcedefinitions/${definitionName}`
      );
    } catch (error) {
      throw new Error(
        `Unable to remove custom resource definition '${definitionName}'`,
        { cause: error }
      );
    }
  }
}
